import { responsiveFontSizes } from '@material-ui/core'
import { createTheme } from '@material-ui/core/styles'
import { merge } from 'lodash'

const pallete = {
	primary: {
		main: '#3B0275'
	},
	secondary: {
		main: '#6B4AD6'
	},
	grey: {
		main: '#4D4C4F'
	},
	danger: {
		main: 'rgba(208, 44, 44, 0.1)'
	}
}

const typography = {
	fontFamily: 'Nunito',
	fontSize: 16,
	h1: {
		fontWeight: 800
	},
	h2: {
		fontWeight: 800
	},
	h3: {
		fontWeight: 800
	},
	h4: {
		fontWeight: 800
	},
	h5: {
		fontWeight: 800
	},
	h6: {
		fontWeight: 800
	},
	subtitle1: {
		fontWeight: 700
	},
	subtitle2: {
		fontWeight: 700
	},
	body1: {
		fontWeight: 400
	},
	body2: {
		fontWeight: 400
	},
	button: {
		fontWeight: 800
	},
	caption: {
		fontWeight: 600,
		fontSize: '14px',
		lineHeight: '19px'
	},
	overline: {
		fontSize: '12px',
		fontWeight: 800,
		letterSpacing: '0.75px'
	}
}

export const mergeTheme = (outerTheme, ...extraThemes) => {
	// Shallow copy of outerTheme
	const newTheme = { ...outerTheme }

	extraThemes.forEach((theme) => {
		newTheme.overrides = merge(newTheme.overrides, theme.overrides)
	})

	return newTheme
}

const mainTheme = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiAutocomplete: {
			tag: {
				backgroundColor: 'rgba(107, 74, 214, 0.1)',
				color: '#6B4AD6'
			},
			label: {
				marginRight: '10px',
				fontFamily: 'Nunito',
				color: '#6B4AD6'
			}
		},
		MuiAppBar: {
			root: {
				backgroundColor: '#3B0275',
				position: 'static',
				borderBottom: '1px solid #D0CFD2',
				color: 'rgba(0,0,0,1)'
			}
		},
		MuiToolbar: {
			root: {
				backgroundColor: 'rgba(255,255,255,1)',
				color: 'rgba(0,0,0,1)',
				height: 60,
				paddingTop: 5,
				paddingBottom: 5
			}
		},
		MuiButton: {
			outlined: {
				borderWidth: 1,
				borderRadius: 40,
				fontWeight: 800,
				'&$disabled': {
					'& .MuiButton-startIcon': {
						'& .MuiSvgIcon-root': {
							fill: 'rgba(0, 0, 0, 0.26) !important'
						}
					}
				}
			},
			outlinedSizeLarge: {
				height: 38,
				'&$disabled': {
					'& .MuiButton-startIcon': {
						'& .MuiSvgIcon-root': {
							fill: 'rgba(0, 0, 0, 0.26) !important'
						}
					}
				}
			},
			contained: {
				borderRadius: 40,
				'&$disabled': {
					color: '#FFFFFF',
					backgroundColor: pallete.secondary.main,
					opacity: '0.6',
					'&$disabled': {
						'& .MuiButton-startIcon': {
							'& .MuiSvgIcon-root': {
								fill: 'rgba(0, 0, 0, 0.26) !important'
							}
						}
					}
				}
			}
		},
		MuiPaper: {
			paper: {
				top: 'auto'
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontWeight: 800,
				fontSize: '12px',
				lineHeight: '16px',
				letterSpacing: '0.75px',
				height: 22
			},
			contained: {
				borderRadius: 40,
				fontWeight: 700
			},
			label: {
				marginRight: '10px'
			}
		},
		MuiMenuItem: {
			root: {
				color: '#000000',
				'&$selected': {
					color: '#000000'
				}
			}
		},
		MuiCard: {
			root: {
				marginBottom: '24px',
				borderRadius: '6px',
				boxShadow: '0px 2px 10px rgba(26, 26, 53, 0.1)'
			}
		},
		MuiCardHeader: {
			root: {
				alignItems: 'center',
				padding: '24px'
			},
			title: {
				fontSize: '16px',
				lineHeight: '22px',
				textTransform: 'uppercase',
				fontWeight: 800
			}
		},
		MuiCardContent: {
			root: {
				padding: '0px 24px'
			}
		},
		MuiCardActions: {
			root: {
				padding: '24px'
			}
		},
		MuiCheckbox: {
			root: {
				'& .MuiIconButton-label .MuiSvgIcon-root': {
					fill: '#D0CFD2'
				},
				'&.Mui-checked .MuiIconButton-label .MuiSvgIcon-root': {
					fill: pallete.secondary.main
				}
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontSize: '12px',
				lineHeight: '16px',
				padding: '3px 8px',
				height: '22px',
				marginRight: 2
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0,
				marginRight: '10px'
			}
		},
		MuiDialog: {
			paper: {
				padding: '24px'
			}
		},
		MuiDialogTitle: {
			root: {
				padding: 0,
				'& h2': {
					fontSize: '16px'
					// lineHeight: '22px'
				}
			}
		},
		MuiDialogContent: {
			root: {
				padding: 0,
				'& .MuiTextField-root': {
					minWidth: '420px'
				},
				'& .MuiTextField-root.noMinWidth': {
					minWidth: 'unset',
					'& .MuiFilledInput-underline': {
						minWidth: 'unset'
					}
				},
				'& .MuiFilledInput-underline': {
					minWidth: '420px'
				},
				'&::-webkit-scrollbar-track': {
					boxShadow: 'inset 0 0 0px rgba(0,0,0,0)',
					backgroundColor: '#FFF'
				},
				'&::-webkit-scrollbar': {
					width: '8px',
					backgroundColor: '#F5F5F5'
				},
				'&::-webkit-scrollbar-thumb': {
					borderRadius: '8px',
					boxShadow: 'inset 0 0 0px rgba(0,0,0,0)',
					backgroundColor: '#D0CFD2'
				}
			}
		},
		MuiDialogActions: {
			root: {
				padding: 0,
				'& button': {
					paddingLeft: '32px',
					paddingRight: '32px'
				}
			}
		},
		MuiTextField: {
			root: {
				minWidth: 420
			}
		},
		MuiSelect: {
			root: {
				minWidth: 200,
				'&:focus': {
					backgroundColor: 'transparent'
				},
				'&$disabled': {
					backgroundColor: 'transparent',
					'& > div': {
						backgroundColor: 'rgba(26, 26, 53, 0.05)',
						'& > span': {
							color: '#A2A1A4'
						}
					}
				}
			},
			icon: {
				fill: '#777679'
			},
			select: {
				'&:focus': {
					backgroundColor: 'transparent'
				}
			},
			filled: {
				'& p': {
					lineHeight: '28px'
				}
			}
		},
		MuiFormControl: {
			root: {
				'& .MuiFormLabel-root': {
					transform: 'translate(12px, 20px) scale(1)',
					zIndex: 998
				},
				'& .MuiInputLabel-shrink': {
					transform: 'translate(12px, 10px) scale(0.75)',
					zIndex: 998
				}
			}
		},
		MuiFilledInput: {
			root: {
				borderTop: '1px solid #D0CFD2',
				borderLeft: '1px solid #D0CFD2',
				borderRight: '1px solid #D0CFD2',
				backgroundColor: 'transparent',
				borderTopLeftRadius: '6px',
				borderTopRightRadius: '6px',
				'&:hover': {
					backgroundColor: 'transparent'
				},
				'&$focused': {
					backgroundColor: 'transparent'
				},
				'&$disabled': {
					borderTop: '1px solid #D0CFD2',
					borderLeft: '1px solid #D0CFD2',
					borderRight: '1px solid #D0CFD2',
					backgroundColor: '#F9F9F9'
				}
			},
			input: {
				padding: '23px 12px 4px',
				fontSize: '16px',
				lineHeight: '28px',
				minHeight: '29px'
			},
			adornedStart: {
				paddingLeft: '2px',
				paddingRight: '5px'
			},
			underline: {
				'&:before': {
					borderBottom: '1px solid #A2A1A4'
				},
				'&:hover:before': {
					borderBottom: '2px solid #A2A1A4'
				},
				'&:after': {
					borderBottom: `2px solid ${pallete.secondary.main}`
				},
				'&$disabled:before': {
					borderBottomStyle: 'solid',
					borderBottomColor: '#D0CFD2'
				}
			}
		},
		MuiInputAdornment: {
			root: {
				marginTop: '0px !important'
			}
		},
		MuiInputBase: {
			root: {
				'&$disabled': {
					backgroundColor: 'transparent'
				}
			}
		}
	}
})

const dogAlertTheme = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiTooltip: {
			tooltip: {
				backgroundColor: '#FFFFFF',
				color: 'rgba(0, 0, 0, 0.87)',
				boxShadow: '0px 2px 15px rgba(26, 26, 53, 0.2)',
				fontSize: 11
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontWeight: 800,
				fontSize: '12px',
				lineHeight: '16px',
				letterSpacing: '0.75px',
				height: 22,
				marginRight: 2,
				padding: '3px 8px'
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0,
				textTransform: 'uppercase'
			}
		}
	}
})

const selectChipTheme = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontWeight: 600,
				fontSize: '14px',
				lineHeight: '19px',
				height: 22,
				padding: '3px 4px 3px 8px',
				background: 'rgba(107, 74, 214, 0.1)',
				backgroundColor: 'rgba(107, 74, 214, 0.1)',
				'& .MuiChip-deleteIcon': {
					margin: 0,
					width: '12px',
					height: '12px',
					fontSize: '12px',
					margin: '0px !important',
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					'&::before': {
						position: 'absolute',
						top: '-1px',
						left: '1.5px',
						height: '13px',
						color: '#5F38BE'
					}
				},
				'& .MuiChip-dangerIcon': {
					margin: 0,
					width: '12px',
					height: '12px',
					fontSize: '12px',
					margin: '0px !important',
					position: 'relative',
					'&::before': {
						position: 'absolute',
						top: '-1px',
						left: '1.5px',
						height: '13px'
					}
				},
				marginRight: 4
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0,
				marginRight: '10px',
				fontWeight: 600,
				fontSize: '14px',
				lineHeight: '19px',
				color: '#5F38BE'
			},
			deleteIcon: {
				margin: 0,
				height: '12px',
				width: '12px',
				color: '#5F38BE'
			}
		}
	}
})

export const light = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiButton: {
			outlined: {
				borderWidth: 1,
				borderRadius: 40,
				fontWeight: 700
			},
			outlinedSizeLarge: {
				height: 38
			},
			contained: {
				borderRadius: 40,
				'&$disabled': {
					color: '#FFFFFF',
					backgroundColor: pallete.secondary.main,
					opacity: '0.6'
				}
			}
		},
		MuiFormLabel: {
			root: {
				'&$focused': {
					color: '#FFFFFF'
				}
			}
		},
		MuiInputLabel: {
			root: {
				color: '#FFFFFF'
			},
			shrink: {
				color: '#FFFFFF',
				opacity: 1
			}
		},
		MuiFilledInput: {
			root: {
				backgroundColor: 'rgba(255, 255, 255, 0.1)',
				borderTopLeftRadius: '6px',
				borderTopRightRadius: '6px',
				'&:hover': {
					backgroundColor: 'rgba(255, 255, 255, 0.1)'
				},
				'&$focused': {
					backgroundColor: 'rgba(255, 255, 255, 0.1)'
				},
				'&$disabled': {
					borderTop: '1px solid #D0CFD2',
					borderLeft: '1px solid #D0CFD2',
					borderRight: '1px solid #D0CFD2',
					backgroundColor: 'rgba(255, 255, 255, 0.1)'
				}
			},
			input: {
				padding: '23px 12px 4px',
				fontSize: '16px',
				lineHeight: '28px',
				minHeight: '29px'
			},
			underline: {
				'&:after': {
					borderBottom: `2px solid #FFFFFF`
				},
				'&:before': {
					borderBottom: `1px solid rgba(255,255,255,0.4)`
				},
				'&:hover:before': {
					borderBottom: `1px solid #FFFFFF`
				},
				'&$disabled:before': {
					borderBottomStyle: 'solid',
					borderBottomColor: '#FFFFFF'
				}
			}
		}
	}
})

export const lightBg = {
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiButton: {
			root: {
				borderRadius: 25,
				padding: '14px 32px'
			},
			outlined: {
				padding: '7px 24px'
			}
		},
		MuiFilledInput: {
			root: {
				borderTop: '1px solid #D0CFD2',
				borderLeft: '1px solid #D0CFD2',
				borderRight: '1px solid #D0CFD2',
				backgroundColor: 'transparent'
			}
		},
		MuiSelect: {
			root: {
				minWidth: 200
			},
			icon: {
				fill: '#777679'
			}
		},
		MuiTextField: {
			root: {
				minWidth: 200
			}
		},
		MuiMenuItem: {
			root: {
				color: '#4D4C4F',
				'&:hover': {
					color: '#4D4C4F'
				}
			}
		},
		MuiInputLabel: {
			root: {
				'&$error': {
					color: 'rgba(0, 0, 0, 0.54)'
				}
			},
			shrink: {
				color: pallete.secondary.main,
				fontWeight: 700
			}
		}
	}
}

export const navigation = {
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiListItemIcon: {
			root: {
				minWidth: 35
			}
		},
		MuiSvgIcon: {
			root: {
				fill: 'rgba(255,255,255,0.6)'
			}
		},
		MuiListItem: {
			root: {
				color: 'rgba(255,255,255,0.6)',
				fontFamily: 'Nunito',
				position: 'relative',
				paddingLeft: 24,
				fontWeight: 'bold',
				'&:hover': {
					backgroundColor: 'rgba(107, 74, 214, 0.1)'
				},
				'&.active': {
					opacity: 1,
					backgroundColor: 'rgba(107, 74, 214, 0.1)'
				}
			},
			gutters: {
				paddingLeft: 24
			}
		},
		MuiDivider: {
			root: {
				backgroundColor: '#6B4AD6'
			}
		},
		MuiListItemText: {
			root: {
				cursor: 'pointer',
				userSelect: 'none',
				fontFamily: 'Nunito'
			}
		}
	}
}

const tableTheme = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiTooltip: {
			tooltip: {
				backgroundColor: '#FFFFFF',
				color: 'rgba(0, 0, 0, 0.87)',
				boxShadow: '0px 2px 15px rgba(26, 26, 53, 0.2)',
				fontSize: 11
			}
		},
		MuiToolbar: {
			root: {
				paddingRight: '16px !important',
				paddingLeft: '0px !important' // this is a very annoying setting and should be removed
			}
		},
		MuiMenuItem: {
			root: {
				color: '#000000',
				'&$selected': {
					color: '#000000'
				}
			}
		},
		MuiButton: {
			root: {
				borderRadius: 25,
				padding: '14px 32px'
			},
			contained: {
				backgroundColor: '#FFFFFF',
				color: pallete.primary.main
			},
			label: {
				fontSize: '14px',
				'& span svg': {
					fontSize: '24px !important'
				},
				'& span:nth-child(2)': {
					marginTop: '2px'
				}
			}
		},
		MuiFilledInput: {
			root: {
				borderTop: '1px solid #D0CFD2',
				borderLeft: '1px solid #D0CFD2',
				borderRight: '1px solid #D0CFD2',
				backgroundColor: 'transparent',
				borderTopLeftRadius: '6px',
				borderTopRightRadius: '6px'
			},
			input: {
				padding: '10px 12px 10px'
			},
			adornedEnd: {
				paddingRight: '0px'
			}
		},
		MuiInputAdornment: {
			positionStart: {
				marginTop: '0px !important',
				color: '#777679'
			},
			positionEnd: {
				color: '#777679'
			}
		},
		MuiTableRow: {
			root: {
				'&:nth-child(even)': {
					background: '#F9F9F9'
				},
				'&:nth-child(odd)': {
					background: '#FDFDFD'
				}
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontWeight: 800,
				fontSize: '12px',
				lineHeight: '16px',
				letterSpacing: '0.75px',
				height: 22,
				marginRight: 2,
				padding: '3px 8px'
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0,
				textTransform: 'uppercase'
			}
		},
		MuiTable: {
			root: {
				backgroundColor: '#F9F9F9'
			}
		}
	}
})

const tablePlayGroupsTheme = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiMenu: {
			paper: {
				top: '740px !important'
			}
		},
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiTooltip: {
			tooltip: {
				backgroundColor: '#FFFFFF',
				color: 'rgba(0, 0, 0, 0.87)',
				boxShadow: '0px 2px 15px rgba(26, 26, 53, 0.2)',
				fontSize: 11
			}
		},
		MuiToolbar: {
			root: {
				paddingRight: '16px !important',
				paddingLeft: '0px !important'
			}
		},
		MuiMenuItem: {
			root: {
				color: '#000000',
				'&$selected': {
					color: '#000000'
				}
			}
		},
		MuiButton: {
			root: {
				borderRadius: 25,
				padding: '14px 32px'
			},
			contained: {
				backgroundColor: '#FFFFFF',
				color: pallete.primary.main
			},
			label: {
				fontSize: '14px',
				'& span svg': {
					fontSize: '24px !important'
				},
				'& span:nth-child(2)': {
					marginTop: '2px'
				}
			}
		},
		MuiFilledInput: {
			root: {
				borderTop: '1px solid #D0CFD2',
				borderLeft: '1px solid #D0CFD2',
				borderRight: '1px solid #D0CFD2',
				backgroundColor: 'transparent',
				borderTopLeftRadius: '6px',
				borderTopRightRadius: '6px'
			},
			input: {
				padding: '10px 12px 10px'
			},
			adornedEnd: {
				paddingRight: '0px'
			}
		},
		MuiInputAdornment: {
			positionStart: {
				marginTop: '0px !important',
				color: '#777679'
			},
			positionEnd: {
				color: '#777679'
			}
		},
		MuiTableRow: {
			root: {
				'&:nth-child(even)': {
					background: '#F9F9F9'
				},
				'&:nth-child(odd)': {
					background: '#FDFDFD'
				}
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontWeight: 800,
				fontSize: '12px',
				lineHeight: '16px',
				letterSpacing: '0.75px',
				height: 22,
				marginRight: 2,
				padding: '3px 8px'
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0,
				textTransform: 'uppercase'
			}
		},
		MuiTable: {
			root: {
				backgroundColor: '#F9F9F9',
				marginBottom: '60px',
				paddingBottom: '20px'
			}
		}
	}
})

const nonAlternatingTableLight = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiTableRow: {
			root: {
				background: '#FFFFFF !important',
				fontSize: '14px'
			}
		},
		MuiTableCell: {
			body: {
				fontSize: '14px'
			},
			root: {
				fontSize: '14px'
			}
		}
	}
})

const nonAlternatingTableDark = createTheme({
	palette: pallete,
	typography: typography,
	overrides: {
		MuiRadio: {
			root: {
				color: '#3B0275'
			},
			colorSecondary: {
				'&$checked': {
					color: '#3B0275'
				}
			}
		},
		MuiTableRow: {
			root: {
				background: '#F9F9F9',
				borderRadius: '6px',
				'&:hover': {
					backgroundColor: '#F3F3F3'
				}
			}
		},
		MuiTableCell: {
			head: {
				paddingLeft: '0px !important'
			},
			body: {
				paddingLeft: '8px !important',
				fontSize: '16px',
				lineHeight: '22px',
				borderBottom: 'none'
			}
		},
		MuiChip: {
			root: {
				borderRadius: 6,
				fontSize: '12px',
				lineHeight: '16px',
				padding: '3px 8px',
				height: '22px',
				marginRight: 2
			},
			label: {
				paddingLeft: 0,
				paddingRight: 0
			}
		}
	}
})

export const main = responsiveFontSizes(mainTheme)
export const dogAlert = responsiveFontSizes(dogAlertTheme)
export const table = responsiveFontSizes(tableTheme)
export const tablePlayGroups = responsiveFontSizes(tablePlayGroupsTheme)
export const tableAlternateLight = responsiveFontSizes(nonAlternatingTableLight)
export const tableAlternateDark = responsiveFontSizes(nonAlternatingTableDark)
export const selectChip = responsiveFontSizes(selectChipTheme)
